import { useSliderThemeHooks } from "hooks/sliderThemeHooks";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { IsochroneDialogProps } from "./interfaces";

export const useIsochroneDialogHooks = (props: IsochroneDialogProps) => {
  const titRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const { sliderTheme } = useSliderThemeHooks();

  const [walking, setWalking] = useState<boolean>(true);
  const [cycling, setCycling] = useState<boolean>(false);
  const [driving, setDriving] = useState<boolean>(false);
  const [minutes, setMinutes] = useState<number>(10);

  const formattedMinutes = useMemo(() => `${minutes} 分`, [minutes]);

  const okButtonOn = useMemo(
    () => walking || cycling || driving || minutes > 0,
    [cycling, driving, minutes, walking]
  );

  const onWalkingChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setWalking(checked);
      if (checked) {
        setCycling(false);
        setDriving(false);
      }
    },
    [setWalking]
  );

  const onCyclingChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setCycling(checked);
      if (checked) {
        setWalking(false);
        setDriving(false);
      }
    },
    [setCycling]
  );

  const onDrivingChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setDriving(checked);
      if (checked) {
        setWalking(false);
        setCycling(false);
      }
    },
    [setDriving]
  );

  const onMinutesChange: (
    event: Event,
    value: number,
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      setMinutes(value);
    },
    [setMinutes]
  );

  const onOkButtonClick = useCallback(() => {
    props.onOkButtonClick?.(walking, cycling, driving, minutes);
    if (props.onClose) {
      props.onClose();
    }
  }, [cycling, driving, minutes, props, walking]);

  const onBackPaperClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (titRef.current && boxRef.current) {
        if (
          titRef.current.contains(event.target as Node) ||
          boxRef.current.contains(event.target as Node)
        ) {
          return;
        }

        if (props.onClose) {
          props.onClose();
        }
      }
    },
    [props]
  );

  useEffect(() => {
    if (!props.open) {
      setTimeout(() => {
        setWalking(true);
        setCycling(false);
        setDriving(false);
        setMinutes(10);
      }, 0);
    }
  }, [props.open]);

  return {
    sliderTheme,

    titRef,
    boxRef,

    walking,
    cycling,
    driving,
    minutes,

    formattedMinutes,

    okButtonOn,

    onWalkingChange,
    onCyclingChange,
    onDrivingChange,
    onMinutesChange,

    onOkButtonClick,
    onBackPaperClick
  } as const;
};

export type UseIsochroneDialogHooksType = ReturnType<
  typeof useIsochroneDialogHooks
>;
